
import { useEffect } from 'react';

interface PrivacyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function PrivacyModal({ isOpen, onClose }: PrivacyModalProps) {
  // ESC tuşu ile kapatma
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      ></div>
      
      {/* Modal */}
      <div className="relative w-full max-w-4xl max-h-[90vh] bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-white/20 overflow-hidden">
        {/* Header */}
        <div className="sticky top-0 bg-white/90 backdrop-blur-md border-b border-gray-200/50 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Gizlilik Politikası
          </h2>
          <button
            onClick={onClose}
            className="w-10 h-10 bg-gray-100/80 hover:bg-gray-200/80 rounded-full flex items-center justify-center transition-colors cursor-pointer"
            title="Kapat"
          >
            <i className="ri-close-line text-xl text-gray-600"></i>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          <div className="prose prose-gray max-w-none">
            <p className="text-sm text-gray-600 mb-6">
              <strong>Son Güncelleme:</strong> 1 Kasım 2025
            </p>

            <p className="text-gray-700 mb-6 leading-relaxed">
              Bu gizlilik politikası, www.guventar.com / GüvenTar tarafından işletilen www.guventar.com adresli internet sitesi ("Site") üzerinden toplanan kişisel verilerin işlenmesine ilişkin esasları açıklamaktadır.
              Siteyi ziyaret eden veya hizmetlerimizden yararlanan her kullanıcı, bu Gizlilik Politikasında yer alan koşulları okumuş ve kabul etmiş sayılır.
            </p>

            <div className="space-y-8">
              {/* 1. Toplanan Bilgiler */}
              <section>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">1</span>
                  Toplanan Bilgiler
                </h3>
                <div className="space-y-3 text-gray-700 leading-relaxed">
                  <p>Sitemizi ziyaret ettiğinizde veya hizmetlerimizden yararlandığınızda aşağıdaki türde bilgiler toplanabilir:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Kimlik Bilgileri:</strong> Ad, soyad, T.C. kimlik numarası (formlarda belirtilmişse)</li>
                    <li><strong>İletişim Bilgileri:</strong> E-posta adresi, telefon numarası, adres</li>
                    <li><strong>Teknik Veriler:</strong> IP adresi, tarayıcı türü, cihaz bilgisi, oturum süresi</li>
                    <li><strong>Kullanım Verileri:</strong> Ziyaret edilen sayfalar, form doldurma bilgileri, hizmet tercihleri</li>
                  </ul>
                  <p>Bu bilgiler, yalnızca belirtilen amaçlar doğrultusunda ve KVKK hükümlerine uygun biçimde toplanır.</p>
                </div>
              </section>

              {/* 2. Verilerin İşlenme Amaçları */}
              <section>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">2</span>
                  Verilerin İşlenme Amaçları
                </h3>
                <div className="space-y-3 text-gray-700 leading-relaxed">
                  <p>Toplanan kişisel veriler aşağıdaki amaçlarla işlenebilir:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Hizmet ve taleplerin yerine getirilmesi</li>
                    <li>Üyelik ve başvuru işlemlerinin yönetilmesi</li>
                    <li>Kullanıcı deneyiminin geliştirilmesi ve kişiselleştirilmesi</li>
                    <li>Yasal yükümlülüklerin yerine getirilmesi</li>
                    <li>İstatistiksel analizler ve sistem güvenliğinin sağlanması</li>
                  </ul>
                </div>
              </section>

              {/* 3. Kişisel Verilerin Aktarımı */}
              <section>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">3</span>
                  Kişisel Verilerin Aktarımı
                </h3>
                <div className="space-y-3 text-gray-700 leading-relaxed">
                  <p>Kişisel verileriniz, aşağıdaki durumlar haricinde üçüncü kişilerle paylaşılmaz:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Kanunen yetkili kamu kurum ve kuruluşlarına talep halinde</li>
                    <li>Hizmetin sağlanabilmesi için iş ortaklarına (örneğin kargo, banka, teknik destek sağlayıcı)</li>
                    <li>Açık rızanızın bulunması halinde ilgili taraflara</li>
                  </ul>
                  <p>Tüm paylaşımlar KVKK madde 8 ve 9 uyarınca yasal güvenlik önlemleri alınarak yapılır.</p>
                </div>
              </section>

              {/* 4. Çerez Kullanımı */}
              <section>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">4</span>
                  Çerez (Cookie) Kullanımı
                </h3>
                <div className="space-y-3 text-gray-700 leading-relaxed">
                  <p>Sitemizde, kullanıcı deneyimini geliştirmek amacıyla çerezler (cookies) kullanılmaktadır.</p>
                  <p>Çerezler, ziyaret ettiğiniz sayfaları, tercihlerinizi ve oturum bilgilerinizi hatırlayarak size daha iyi bir hizmet sunmamıza yardımcı olur.</p>
                  <p>Tarayıcı ayarlarınızdan çerezleri kabul etmeme veya silme seçeneğiniz bulunmaktadır.</p>
                </div>
              </section>

              {/* 5. Veri Güvenliği */}
              <section>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">5</span>
                  Veri Güvenliği
                </h3>
                <div className="space-y-3 text-gray-700 leading-relaxed">
                  <p>Kişisel verileriniz, izinsiz erişim, kayıp veya kötüye kullanıma karşı korunmak üzere gerekli teknik ve idari önlemler alınarak saklanmaktadır.</p>
                  <p>Veriler yalnızca yetkili personel tarafından erişilebilir ve gizlilik esasına uygun olarak işlenir.</p>
                </div>
              </section>

              {/* 6. Saklama Süresi */}
              <section>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">6</span>
                  Kişisel Verilerin Saklanma Süresi
                </h3>
                <div className="space-y-3 text-gray-700 leading-relaxed">
                  <p>Kişisel verileriniz, işlenme amacının ortadan kalkması veya yasal sürelerin dolması halinde silinir, yok edilir veya anonim hale getirilir.</p>
                </div>
              </section>

              {/* 7. Kullanıcı Hakları */}
              <section>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">7</span>
                  Kullanıcı Hakları
                </h3>
                <div className="space-y-3 text-gray-700 leading-relaxed">
                  <p>6698 sayılı KVKK uyarınca kullanıcılar aşağıdaki haklara sahiptir:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Kişisel verilerinin işlenip işlenmediğini öğrenme</li>
                    <li>İşlenmişse buna ilişkin bilgi talep etme</li>
                    <li>Amacına uygun kullanılıp kullanılmadığını öğrenme</li>
                    <li>Eksik veya yanlış işlenmişse düzeltilmesini isteme</li>
                    <li>Silinmesini veya yok edilmesini talep etme</li>
                    <li>Verilerin aktarıldığı üçüncü kişilerin bilgilendirilmesini isteme</li>
                  </ul>
                  <p>Bu haklardan herhangi birini kullanmak için bizimle iletişime geçebilirsiniz.</p>
                </div>
              </section>

              {/* 8. İletişim */}
              <section>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">8</span>
                  İletişim
                </h3>
                <div className="space-y-3 text-gray-700 leading-relaxed">
                  <p>Kişisel verilerinizle ilgili tüm soru, talep veya başvurularınızı aşağıdaki iletişim kanallarından bize iletebilirsiniz:</p>
                  <div className="bg-gray-50/80 backdrop-blur-sm rounded-xl p-4 mt-4">
                    <div className="space-y-2">
                      <p><strong>GüvenTar</strong></p>
                      <p className="flex items-start">
                        <i className="ri-map-pin-line text-green-600 mr-2 mt-0.5"></i>
                        <span><strong>Adres:</strong> Cumalı Mh. Antalya Cd. No:9 Serik/Antalya</span>
                      </p>
                      <p className="flex items-center">
                        <i className="ri-mail-line text-green-600 mr-2"></i>
                        <strong>E-posta:</strong> bilgi@guventar.com
                      </p>
                      <p className="flex items-center">
                        <i className="ri-phone-line text-green-600 mr-2"></i>
                        <strong>Telefon:</strong> +90 553 414 52 63
                      </p>
                    </div>
                  </div>
                  <p>Başvurularınız en geç 30 gün içinde yanıtlanacaktır.</p>
                </div>
              </section>

              {/* 9. Değişiklik Hakkı */}
              <section>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">9</span>
                  Değişiklik Hakkı
                </h3>
                <div className="space-y-3 text-gray-700 leading-relaxed">
                  <p>Bu gizlilik politikası, hizmet ve yasal gerekliliklere göre zaman zaman güncellenebilir.</p>
                  <p>Güncel sürüm her zaman bu sayfada yayımlanır.</p>
                </div>
              </section>

              {/* Final Note */}
              <div className="bg-green-50/80 backdrop-blur-sm rounded-xl p-4 mt-8">
                <p className="text-gray-700 leading-relaxed">
                  <strong>İşbu Gizlilik Politikası, 6698 sayılı Kişisel Verilerin Korunması Kanunu hükümleri uyarınca hazırlanmıştır.</strong><br />
                  Siteyi kullanmaya devam etmeniz, bu politikayı kabul ettiğiniz anlamına gelir.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white/90 backdrop-blur-md border-t border-gray-200/50 px-6 py-4">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-xl font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-check-line mr-2"></i>
              Anladım
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
